'use client';

import { isEmpty } from 'lodash';
import moment from 'moment';
import type React from 'react';
import { useCallback } from 'react';
import { Button } from 'reactstrap';

import Description from 'src/components/job-slide-out/components/description';
import FlagSummaryBanner from 'src/components/job-slide-out/components/flag-summary-banner';
import HvacUnitInfo from 'src/components/job-slide-out/components/hvac-unit-info';
import PhotosContainer from 'src/components/job-slide-out/components/photos';
import ResidentResponsibility from 'src/components/job-slide-out/components/resident-responsibility';
import ReviewContainer from 'src/components/job-slide-out/components/review';
import TenantAddress from 'src/components/job-slide-out/components/tenant-address';
import TenantInfo from 'src/components/job-slide-out/components/tenant-info-section';
import { isMakeReadyServiceCategory, updateUrlForOpenJobSidePanel } from 'src/helpers/job-slide-out';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import useFeatureFlags from 'src/hooks/use-feature-flags';
import { updateSlideOut } from 'src/redux/jobs/slice';
import { updateVisibility } from 'src/redux/modals/slice';
import SlideOutDataCommonWrapper from '../slideout-data-common-wrapper';

const BasicInfo: React.FC = () => {
  const job = useAppSelector((state) => state?.jobDetails.data);
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const isLoading = useAppSelector((state) => state?.jobDetails?.isLoading);
  const timeZone = useAppSelector((state) => state?.jobDetails?.data?.timezone);
  const masterStatus = useAppSelector((state) => state?.jobDetails?.data?.master_status);
  const {
    residentResponsibilityEdit: isResidentResponsibilityEditEnabled,
    makeReadySlideout,
  } = useFeatureFlags();
  const scrollToLumiIntake = useAppSelector((state) => state?.jobDetails?.slideOut?.scrollToLumiIntake);

  const dispatch = useAppDispatch();

  const openExternalWorkOrderDetails = useCallback(
    () => {
      const externalJobSource = job?.external_job_source;
      if (externalJobSource?.link) {
        window.open(externalJobSource?.link, '_blank');
      }
    },
    [job?.external_job_source],
  );

  const handleOpenEscalationDetails = useCallback(() => {
    dispatch(updateVisibility({
      modalType: 'escalationStatusModal',
      isVisible: true,
      extraProps: {
        escalationId: job?.property_escalation?.property_escalation_id || '',
        type: 'escalation',
        timezone: timeZone || '',
      },
    }));
  }, [dispatch, job?.property_escalation?.property_escalation_id, timeZone]);

  const handOpenMaintenanceHistory = useCallback(() => {
    dispatch(updateVisibility({
      modalType: 'maintenanceHistoryModal',
      isVisible: true,
      extraProps: { serviceRequestNo: job?.service_request_no },
    }));
  }, [dispatch, job?.service_request_no]);

  const onServiceRequestNoClick = useCallback(() => {
    updateUrlForOpenJobSidePanel(
      job?.service_request_no,
      'serviceRequestDetails',
      '',
      job?.service_request_id,
    );
  }, [job?.service_request_id, job?.service_request_no]);

  const renderMaintenanceNumber = useCallback(
    () => {
      if (type === 'jobDetails') {
        return (
          <div className="copy-data-mr">
            <Button
              type="button"
              color="link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onServiceRequestNoClick();
              }}
            >
              #
              {(job?.service_request_no as string) || ''}
            </Button>
          </div>
        );
      }
      if (type === 'serviceRequestDetails') {
        return (
          <p>
            {`#${job?.service_request_no || ''}`}
          </p>
        );
      }
      return '';
    },
    [job?.service_request_no, onServiceRequestNoClick, type],
  );

  const externalJobSource = job?.external_job_source;
  const jobDescriptions = job?.job_descriptions;
  const jobReviews = job?.job_reviews;

  const showResidentResponsibility =
    isResidentResponsibilityEditEnabled &&
    type === 'jobDetails' &&
    (masterStatus?.slug === 'invoice_ready' || masterStatus?.slug === 'closed');

  const isMakeReadyJob = makeReadySlideout && isMakeReadyServiceCategory(job?.type, job?.service_category?.slug);


  return (
			<div className="job-content-row" id="job-content-row">
        {showResidentResponsibility && <ResidentResponsibility />}
				{job?.property_escalation?.is_escalated && (
					<div className="escalation-banner">
						<div className="escalation-banner__top">
							<div className="escalation-banner__pill">
								<svg
									width="12"
									height="12"
									viewBox="0 0 12 12"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M2 7.5C2 7.5 2.5 7 4 7C5.5 7 6.5 8 8 8C9.5 8 10 7.5 10 7.5V1.5C10 1.5 9.5 2 8 2C6.5 2 5.5 1 4 1C2.5 1 2 1.5 2 1.5L2 11"
										stroke="white"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								<span>Escalated</span>
							</div>
							{job?.property_escalation?.property_escalation_id ? (
								<button
									type="button"
									className="escalation-banner__view-btn"
									onClick={handleOpenEscalationDetails}
								>
									View Details
								</button>
							) : (
								''
							)}
						</div>
						{job?.property_escalation?.reason ? (
							<p className="escalation-banner__reason">
								<strong>Escalation Reason:</strong>
								{` ${job?.property_escalation?.reason}`}
							</p>
						) : null}
					</div>
				)}
				<FlagSummaryBanner />
				{type === 'serviceRequestDetails' ? (
					<SlideOutDataCommonWrapper
						label="Address:"
						loaderClass="common-loading w-15"
						isLoading={isLoading}
						showLoader={type === 'serviceRequestDetails'}
						shouldShow={job?.service_request_no}
					>
						<TenantAddress job={job} />
						{job?.maintenance_history_count ? (
							<Button
								type="button"
								onClick={handOpenMaintenanceHistory}
								color="none"
								className="grey-outline-btn"
							>
								Maintenance History ({job?.maintenance_history_count})
							</Button>
						) : null}
					</SlideOutDataCommonWrapper>
				) : (
					''
				)}
				<SlideOutDataCommonWrapper
					label="Maintenance Request ID:"
					loaderClass="common-loading w-15"
					isLoading={isLoading}
					showLoader={type === 'jobDetails' || type === 'serviceRequestDetails'}
					shouldShow={job?.service_request_no}
				>
					{renderMaintenanceNumber()}
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="PM WO ID"
					loaderClass="common-loading w-15"
					isLoading={isLoading}
					showLoader={type === 'jobDetails' || type === 'serviceRequestDetails'}
					shouldShow={!isEmpty(externalJobSource)}
				>
					<Button
						type="button"
						color="link"
						onClick={openExternalWorkOrderDetails}
					>
						{`${externalJobSource?.label || ''} #${externalJobSource?.reference_no || ''}`}
					</Button>
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="Job Description:"
					loaderClass="common-loading-lg"
					isLoading={isLoading}
					showLoader={type === 'jobDetails' || type === 'serviceRequestDetails'}
					shouldShow={jobDescriptions?.length}
				>
					<Description />
					{job?.has_lumi_summary &&
					<Button
						type="button"
						color="none"
						className="view-conversation-summary-btn"
						disabled={scrollToLumiIntake}
						onClick={() =>
							dispatch(
								updateSlideOut({
									activeTab: 'activityLog',
									scrollToLumiIntake: true,
								}),
							)
						}
					>
						{scrollToLumiIntake ? (
							<span className="view-conversation-summary-btn__loader" />
						) : (
							<svg
								width="12"
								height="12"
								viewBox="0 0 12 12"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g clip-path="url(#clip0_2005_26972)">
									<path
										d="M9.75 1L9.75 3.5M9.75 8.5L9.75 11M11 9.75L8.5 9.75M11 2.25L8.5 2.25M5.5 10.5L6.36709 8.24557C6.50809 7.87895 6.5786 7.69565 6.68824 7.54146C6.78541 7.4048 6.9048 7.28541 7.04146 7.18824C7.19565 7.0786 7.37895 7.0081 7.74557 6.86709L10 6L7.74557 5.13291C7.37895 4.99191 7.19565 4.9214 7.04146 4.81177C6.9048 4.7146 6.78541 4.5952 6.68824 4.45854C6.5786 4.30435 6.50809 4.12105 6.36709 3.75443L5.5 1.5L4.63291 3.75443C4.49191 4.12105 4.4214 4.30435 4.31176 4.45854C4.21459 4.5952 4.0952 4.7146 3.95854 4.81177C3.80435 4.9214 3.62105 4.99191 3.25443 5.13291L1 6L3.25443 6.86709C3.62105 7.0081 3.80435 7.0786 3.95854 7.18824C4.0952 7.28541 4.21459 7.4048 4.31177 7.54146C4.4214 7.69565 4.49191 7.87895 4.63291 8.24557L5.5 10.5Z"
										stroke="#6839EF"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</g>
								<defs>
									<clipPath id="clip0_2005_26972">
										<rect
											width="12"
											height="12"
											fill="white"
											transform="translate(12 12) rotate(-180)"
										/>
									</clipPath>
								</defs>
							</svg>
						)}
            View Conversation Summary
					</Button>
					}
				</SlideOutDataCommonWrapper>

				<PhotosContainer
					isLoading={isLoading && type === 'jobDetails'}
					timeZone={timeZone}
				/>

				<SlideOutDataCommonWrapper
					label={!isMakeReadyJob ? "Tenant Info:" : "Address"}
					loaderClass="common-loading w-36"
					isLoading={isLoading}
					showLoader={type === 'jobDetails' || type === 'serviceRequestDetails'}
					shouldShow={!isMakeReadyJob && job?.tenant_info}
				>
					<TenantInfo job={job} isMakeReadyJob={isMakeReadyJob} />
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="Unit Condition Report:"
					loaderClass="common-loading-lg"
					isLoading={isLoading}
					shouldShow={!isEmpty(job?.location_appliances)}
					showLoader={type === 'jobDetails'}
					columnClass="mb-0"
				>
					<HvacUnitInfo />
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="Created:"
					loaderClass="common-loading w-36"
					isLoading={isLoading}
					showLoader={type === 'jobDetails'}
					shouldShow={type === 'jobDetails' && job?.created_at && timeZone}
				>
					<p>
						<span>
							{moment
								?.utc(job?.created_at as string)
								?.tz(timeZone as string)
								?.format('MMM D, YYYY h:mm a z') || ''}
						</span>
					</p>
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="Scheduled:"
					loaderClass="common-loading w-36"
					isLoading={isLoading}
					showLoader={type === 'jobDetails'}
					shouldShow={job?.scheduled_at}
				>
					<p>
						<span>{job?.scheduled_at as string}</span>
					</p>
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="Completion Date:"
					loaderClass="common-loading w-36"
					isLoading={isLoading}
					showLoader={type === 'jobDetails'}
					shouldShow={job?.completion_date}
				>
					<p>
						<span>
							{moment
								?.utc(job?.completion_date as string)
								?.tz(timeZone as string)
								?.format('MMM DD, YYYY h:mm a z') || ''}
						</span>
					</p>
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="Submitted:"
					loaderClass="common-loading w-36"
					isLoading={isLoading}
					showLoader={type === 'serviceRequestDetails'}
					shouldShow={
						job?.created_at && type === 'serviceRequestDetails' && timeZone
					}
				>
					<p>
						<span>
							{moment
								?.utc(job?.created_at as string)
								?.tz(timeZone as string)
								?.format('MMM DD, YYYY h:mm a z') || ''}
						</span>
					</p>
					{/* SENDER TO BE ADDED */}
				</SlideOutDataCommonWrapper>

				<SlideOutDataCommonWrapper
					label="Canceled Date:"
					loaderClass="common-loading w-36"
					isLoading={isLoading}
					showLoader={type === 'jobDetails'}
					shouldShow={job?.canceled_at}
				>
					<p>
						<span>
							{moment
								?.utc(job?.canceled_at, 'YYYY-MM-DD HH:mm:ss')
								?.local()
								?.format('MMM D h:mm a')}
						</span>
					</p>
				</SlideOutDataCommonWrapper>
				<SlideOutDataCommonWrapper
					label={`Job Reviews ${
						jobReviews && jobReviews?.length > 1
							? `(${jobReviews?.length})`
							: ''
					}`}
					loaderClass="common-loading-lg"
					isLoading={isLoading}
					showLoader={type === 'jobDetails'}
					shouldShow={Array.isArray(jobReviews) && !isEmpty(jobReviews)}
				>
					<ReviewContainer job={job} />
				</SlideOutDataCommonWrapper>
			</div>
		);
};
export default BasicInfo;
